import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Home, MapPin, Calculator, Euro, TrendingUp, Info, CheckCircle2, AlertTriangle, Loader2, Building2, Users, Leaf, Shield, PiggyBank, FileText, Sparkles, Target, Award, HelpCircle, RefreshCcw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Slider } from './ui/slider';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

import axios from 'axios';
import { toast } from 'sonner';
import ESSConsentModal, { useESSConsent } from './ESSConsentModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Territoires avec leurs icônes et couleurs
const TERRITOIRES_CONFIG = {
  "Guadeloupe": { icon: "🏝️", color: "from-blue-500 to-cyan-500", code: "971" },
  "Martinique": { icon: "🌴", color: "from-green-500 to-emerald-500", code: "972" },
  "Guyane": { icon: "🌿", color: "from-emerald-500 to-teal-500", code: "973" },
  "La Réunion": { icon: "🌋", color: "from-orange-500 to-red-500", code: "974" },
  "Mayotte": { icon: "🐢", color: "from-teal-500 to-cyan-500", code: "976" }
};

// Types de logement
const TYPES_LOGEMENT = [
  { id: "T1", label: "Studio/T1", surface: "25-35 m²", icon: "🏠" },
  { id: "T2", label: "T2", surface: "35-50 m²", icon: "🏡" },
  { id: "T3", label: "T3", surface: "50-70 m²", icon: "🏘️" },
  { id: "T4", label: "T4", surface: "70-90 m²", icon: "🏰" },
  { id: "T5+", label: "T5 et +", surface: "90+ m²", icon: "🏛️" }
];

export default function SimulateurHabitatOutremer({ onSimulationComplete, showHeader = true }) {
  const { t: t } = useTranslation();
  
  // Hook de consentement ESS
  const { 
    hasConsent, 
    showModal, 
    handleAccept 
  } = useESSConsent('habitat_outremer_v1');
  
  // États du formulaire
  const [territoire, setTerritoire] = useState('');
  const [revenuMensuel, setRevenuMensuel] = useState('');
  const [apport, setApport] = useState('');
  const [typeLogement, setTypeLogement] = useState('T3');
  const [surface, setSurface] = useState(70);
  
  // États de l'application
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Form, 2: Results
  const [simulatorConfig, setSimulatorConfig] = useState(null);
  
  // Charger la configuration du simulateur
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/simulateurs/habitat_outremer_v1`);
        setSimulatorConfig(response.data);
      } catch {
      }
    };
    loadConfig();
  }, []);
  
  // Calculer le taux d'effort en temps réel
  const _tauxEffortEstime = revenuMensuel && result 
    ? ((result.results.redevance_mensuelle / parseFloat(revenuMensuel)) * 100).toFixed(1)
    : null;
  
  // Validation du formulaire
  const isFormValid = territoire && revenuMensuel && parseFloat(revenuMensuel) > 0;
  
  // Lancer la simulation
  const runSimulation = async () => {
    if (!isFormValid) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/simulateurs/run`, {
        simulator_id: 'habitat_outremer_v1',
        inputs: {
          territoire,
          revenu_mensuel: parseFloat(revenuMensuel),
          apport: parseFloat(apport) || 0,
          type_logement: typeLogement,
          surface: surface
        }
      });
      
      if (response.data.status === 'refuse') {
        setError(response.data.message);
        toast.error(response.data.message);
      } else {
        setResult(response.data);
        setStep(2);
        toast.success('Simulation effectuée avec succès !');
        
        if (onSimulationComplete) {
          onSimulationComplete(response.data);
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Erreur lors de la simulation';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Reset la simulation
  const resetSimulation = () => {
    setResult(null);
    setStep(1);
    setError(null);
  };
  
  // Obtenir la couleur du taux d'effort
  const _getEffortColor = (taux) => {
    if (taux <= 25) return 'text-green-600 bg-green-100';
    if (taux <= 33) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };
  
  // Obtenir la couleur de l'indicateur
  const getIndicatorColor = (indicator) => {
    switch (indicator) {
      case 'acceptable':
      case 'ok':
      case 'éligible':
        return 'bg-green-500';
      case 'modéré':
      case 'à_étudier':
      case 'sous_conditions':
        return 'bg-yellow-500';
      default:
        return 'bg-red-500';
    }
  };
  
  // Render du formulaire
  const renderForm = () => (
    <div className="space-y-6">
      {/* Sélection du territoire */}
      <div className="space-y-3">
        <Label className="text-base font-semibold flex items-center gap-2">
          <MapPin className="h-5 w-5 text-blue-600" />
          Territoire *
        </Label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {Object.entries(TERRITOIRES_CONFIG).map(([name, config]) => (
            <button
              key={name}
              type="button"
              onClick={() => setTerritoire(name)}
              className={`p-4 rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                territoire === name
                  ? `border-transparent bg-gradient-to-br ${config.color} text-white shadow-lg`
                  : 'border-gray-200 hover:border-blue-300 bg-white'
              }`}
            >
              <div className="text-2xl mb-1">{config.icon}</div>
              <div className="text-sm font-medium">{name}</div>
              <div className={`text-xs ${territoire === name ? 'text-white/80' : 'text-gray-500'}`}>
                {config.code}
              </div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Revenu mensuel */}
      <div className="space-y-2">
        <Label className="text-base font-semibold flex items-center gap-2">
          <Euro className="h-5 w-5 text-green-600" />
          Revenu mensuel du foyer *
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Revenus nets mensuels de tous les membres du foyer</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Label>
        <div className="relative">
          <Input
            type="number"
            placeholder="Ex: 1800"
            value={revenuMensuel}
            onChange={(e) => setRevenuMensuel(e.target.value)}
            className="pl-10 text-lg h-12"
            min="0"
            max="15000"
          />
          <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        </div>
        {revenuMensuel && parseFloat(revenuMensuel) < 1200 && (
          <p className="text-sm text-amber-600 flex items-center gap-1">
            <AlertTriangle className="h-4 w-4" />
            Le revenu minimum requis est de 1 200 €/mois
          </p>
        )}
      </div>
      
      {/* Apport personnel */}
      <div className="space-y-2">
        <Label className="text-base font-semibold flex items-center gap-2">
          <PiggyBank className="h-5 w-5 text-purple-600" />
          Apport personnel (optionnel)
        </Label>
        <div className="relative">
          <Input
            type="number"
            placeholder="Ex: 10000"
            value={apport}
            onChange={(e) => setApport(e.target.value)}
            className="pl-10 text-lg h-12"
            min="0"
          />
          <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        </div>
      </div>
      
      {/* Type de logement */}
      <div className="space-y-3">
        <Label className="text-base font-semibold flex items-center gap-2">
          <Home className="h-5 w-5 text-indigo-600" />
          Type de logement souhaité
        </Label>
        <div className="grid grid-cols-5 gap-2">
          {TYPES_LOGEMENT.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => setTypeLogement(type.id)}
              className={`p-3 rounded-lg border-2 transition-all ${
                typeLogement === type.id
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                  : 'border-gray-200 hover:border-indigo-200'
              }`}
            >
              <div className="text-xl mb-1">{type.icon}</div>
              <div className="text-sm font-medium">{type.label}</div>
              <div className="text-xs text-gray-500">{type.surface}</div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Surface */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label className="text-base font-semibold flex items-center gap-2">
            <Building2 className="h-5 w-5 text-teal-600" />
            Surface souhaitée
          </Label>
          <Badge variant="secondary" className="text-lg px-3 py-1">
            {surface} m²
          </Badge>
        </div>
        <Slider
          value={[surface]}
          onValueChange={(value) => setSurface(value[0])}
          min={20}
          max={150}
          step={5}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>20 m²</span>
          <span>150 m²</span>
        </div>
      </div>
      
      {/* Message d'erreur */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Simulation non éligible</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {/* Bouton de simulation */}
      <Button
        onClick={runSimulation}
        disabled={!isFormValid || loading}
        className="w-full h-14 text-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Calcul en cours...
          </>
        ) : (
          <>
            <Calculator className="mr-2 h-5 w-5" />
            Lancer la simulation
          </>
        )}
      </Button>
    </div>
  );
  
  // Render des résultats
  const renderResults = () => {
    if (!result) return null;
    
    const { results, indicators, inputs_recap, territoire_info: territoire_info, disclaimer } = result;
    
    return (
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* En-tête des résultats */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-full bg-gradient-to-br ${TERRITOIRES_CONFIG[inputs_recap.territoire]?.color || 'from-blue-500 to-indigo-500'}`}>
              <CheckCircle2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Simulation réussie</h3>
              <p className="text-sm text-gray-500">{inputs_recap.territoire} • {inputs_recap.type_logement} • {inputs_recap.surface} m²</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={resetSimulation}>
            <RefreshCcw className="h-4 w-4 mr-1" />
            Modifier
          </Button>
        </div>
        
        {/* Indicateurs clés */}
        <div className="grid grid-cols-3 gap-4">
          <Card className={`border-2 ${
            indicators.eligibilite === 'éligible' ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'
          }`}>
            <CardContent className="p-4 text-center">
              <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full mb-2 ${
                indicators.eligibilite === 'éligible' ? 'bg-green-500' : 'bg-yellow-500'
              }`}>
                <Target className="h-5 w-5 text-white" />
              </div>
              <p className="text-xs text-gray-600 uppercase tracking-wide">Éligibilité</p>
              <p className={`text-sm font-bold capitalize ${
                indicators.eligibilite === 'éligible' ? 'text-green-700' : 'text-yellow-700'
              }`}>
                {indicators.eligibilite.replace('_', ' ')}
              </p>
            </CardContent>
          </Card>
          
          <Card className={`border-2 ${
            indicators.effort_financier === 'acceptable' ? 'border-green-200 bg-green-50' : 
            indicators.effort_financier === 'modéré' ? 'border-yellow-200 bg-yellow-50' : 'border-red-200 bg-red-50'
          }`}>
            <CardContent className="p-4 text-center">
              <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full mb-2 ${getIndicatorColor(indicators.effort_financier)}`}>
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <p className="text-xs text-gray-600 uppercase tracking-wide">Effort financier</p>
              <p className={`text-sm font-bold capitalize ${
                indicators.effort_financier === 'acceptable' ? 'text-green-700' : 
                indicators.effort_financier === 'modéré' ? 'text-yellow-700' : 'text-red-700'
              }`}>
                {indicators.effort_financier}
              </p>
            </CardContent>
          </Card>
          
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardContent className="p-4 text-center">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-2 bg-blue-500">
                <Award className="h-5 w-5 text-white" />
              </div>
              <p className="text-xs text-gray-600 uppercase tracking-wide">Taux d'effort</p>
              <p className={`text-sm font-bold ${results.taux_effort <= 33 ? 'text-green-700' : 'text-red-700'}`}>
                {results.taux_effort}%
              </p>
            </CardContent>
          </Card>
        </div>
        
        {/* Résultats financiers */}
        <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Résultats de votre simulation
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Coût total estimé</p>
                <p className="text-2xl font-bold text-gray-900">
                  {results.cout_total_estime.toLocaleString('fr-FR')} €
                </p>
              </div>
              
              <div className="text-center p-4 bg-green-50 rounded-xl">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Aide régionale</p>
                <p className="text-2xl font-bold text-green-700">
                  -{results.aide_regionale_estimee.toLocaleString('fr-FR')} €
                </p>
              </div>
              
              <div className="text-center p-4 bg-purple-50 rounded-xl">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Votre apport</p>
                <p className="text-2xl font-bold text-purple-700">
                  -{inputs_recap.apport.toLocaleString('fr-FR')} €
                </p>
              </div>
              
              <div className="text-center p-4 bg-blue-50 rounded-xl">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">À financer</p>
                <p className="text-2xl font-bold text-blue-700">
                  {results.montant_a_financer.toLocaleString('fr-FR')} €
                </p>
              </div>
            </div>
            
            {/* Séparateur */}
            <div className="my-6 border-t border-dashed border-gray-300" />
            
            {/* Redevance mensuelle - Mise en avant */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white text-center">
              <p className="text-sm opacity-90 uppercase tracking-wide mb-2">Redevance mensuelle estimée</p>
              <div className="flex items-center justify-center gap-3">
                <Sparkles className="h-8 w-8 opacity-80" />
                <span className="text-5xl font-bold">
                  {results.redevance_mensuelle.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </span>
                <span className="text-xl opacity-80">/mois</span>
              </div>
              <p className="text-sm opacity-75 mt-2">
                Sur {results.duree_annees} ans ({results.duree_mois} mois)
              </p>
            </div>
            
            {/* Détails supplémentaires */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                <Shield className="h-8 w-8 text-amber-600" />
                <div>
                  <p className="text-xs text-gray-500">Dotation FOGEDOM</p>
                  <p className="text-lg font-bold text-amber-700">
                    {results.dotation_fogedom.toLocaleString('fr-FR')} €/mois
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-rose-50 rounded-lg">
                <AlertTriangle className="h-8 w-8 text-rose-600" />
                <div>
                  <p className="text-xs text-gray-500">Reste à charge max</p>
                  <p className="text-lg font-bold text-rose-700">
                    {results.reste_a_charge_max.toLocaleString('fr-FR')} €
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Disclaimer */}
        <Alert className="border-blue-200 bg-blue-50">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-800">Information importante</AlertTitle>
          <AlertDescription className="text-blue-700">
            <ul className="list-disc list-inside space-y-1 mt-2">
              {disclaimer.map((item, index) => (
                <li key={index} className="text-sm">{item}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
        
        {/* Actions */}
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={resetSimulation}>
            <RefreshCcw className="h-4 w-4 mr-2" />
            Nouvelle simulation
          </Button>
          <Button className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700">
            <FileText className="h-4 w-4 mr-2" />
            Contacter un conseiller
          </Button>
        </div>
      </div>
    );
  };
  
  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Modal de consentement ESS */}
      <ESSConsentModal
        isOpen={showModal && !hasConsent}
        onAccept={handleAccept}
        onClose={() => {}}
        simulatorId="habitat_outremer_v1"
        simulatorName="Simulateur Habitat Solidaire Outre-mer"
      />
      
      <Card className="overflow-hidden shadow-2xl border-0">
        {/* Header premium */}
        {showHeader && (
          <CardHeader className="bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 text-white p-8">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-white/10 rounded-2xl backdrop-blur">
                <Home className="h-10 w-10 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl md:text-3xl font-bold mb-2">
                  Simulateur Habitat Solidaire Outre-mer
                </CardTitle>
                <CardDescription className="text-white/80 text-base">
                  Outil d'aide à la décision coopérative • Dispositif ESS non engageant
                </CardDescription>
              </div>
            </div>
            
            {/* Badges */}
            <div className="flex flex-wrap gap-2 mt-6">
              <Badge className="bg-slate-800 text-white border-slate-600 backdrop-blur font-semibold">
                <Leaf className="h-3 w-3 mr-1" />
                Économie Sociale et Solidaire
              </Badge>
              <Badge className="bg-slate-800 text-white border-slate-600 backdrop-blur font-semibold">
                <Users className="h-3 w-3 mr-1" />
                Dispositif Coopératif
              </Badge>
              <Badge className="bg-slate-800 text-white border-slate-600 backdrop-blur font-semibold">
                <Shield className="h-3 w-3 mr-1" />
                Sans engagement
              </Badge>
            </div>
          </CardHeader>
        )}
        
        {/* Contenu - Affiché uniquement après consentement */}
        <CardContent className="p-6 md:p-8">
          {hasConsent ? (
            step === 1 ? renderForm() : renderResults()
          ) : (
            <div className="text-center py-12">
              <Shield className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                Veuillez accepter les conditions d'utilisation pour accéder au simulateur.
              </p>
            </div>
          )}
        </CardContent>
        
        {/* Footer légal */}
        <CardFooter className="bg-gray-50 border-t px-6 py-4">
          <p className="text-xs text-gray-500 text-center w-full">
            <Info className="h-3 w-3 inline mr-1" />
            {simulatorConfig?.legal_notice || "Simulation indicative – ne constitue ni une offre, ni un engagement contractuel. Dispositif solidaire ESS, hors assurance."}
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
